import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './lib/api';
import { AuthProvider } from './lib/auth';
import { ToastProvider } from './components/ui';
import App from './App';
import { AssistantProvider } from './components/assistant/AssistantContext';
import { AssistantDock } from './components/assistant/AssistantPanel';
import './styles.css';

/** Preview builds (VITE_DEMO=1) replay recorded data instead of calling the API. */
const DEMO = import.meta.env.VITE_DEMO === '1';
const DemoBar = DEMO ? lazy(() => import('./demo/DemoBar').then((m) => ({ default: m.DemoBar }))) : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

function render() {
  const Router = DEMO ? HashRouter : BrowserRouter;
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <ToastProvider>
              {/* The assistant panel: its floating button shows on every staff page, for Circdal staff only. */}
              <AssistantProvider>
                <App />
                <AssistantDock />
              </AssistantProvider>
              {DemoBar ? (
                <Suspense fallback={null}>
                  <DemoBar />
                </Suspense>
              ) : null}
            </ToastProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </StrictMode>,
  );
}

if (DEMO) {
  import('./demo/shim')
    .then(async (m) => {
      await m.installDemo();
      (window as unknown as { __circdalDemoFileUrl: (id: number) => string }).__circdalDemoFileUrl = m.demoFileUrl;
    })
    .then(render);
} else {
  render();
}
